import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

import { ConfigService } from '../config/config.service';

@Injectable({
  providedIn: 'root',
})
export class MetadataService {
  private readonly titleService = inject(Title);
  private readonly metaService = inject(Meta);
  private readonly configService = inject(ConfigService);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);

  /**
   * Manually triggers the metadata update (legacy/shorthand for reset).
   */
  update(): void {
    this.resetMetadata();
  }

  /**
   * Reverts metadata to the defaults defined in the app configuration.
   */
  resetMetadata(): void {
    const config = this.configService.config();
    if (!config) return;

    const { title, description } = config.metadata;
    this.applyRawMetadata(title, description);

    // Update Favicon (Browser only)
    if (isPlatformBrowser(this.platformId)) {
      const clientId = this.configService.activeClientId();
      const faviconUrl = `assets/clients/${clientId}/${config.metadata.favicon}`;
      this.updateFavicon(faviconUrl);
    }
  }

  /**
   * Sets page-specific metadata.
   * Example: setPageMetadata('Product Name', 'Product description...')
   * Resulting title: "Product Name | Pyrodex"
   */
  setPageMetadata(title: string, description?: string): void {
    const config = this.configService.config();
    const siteName = config?.metadata.title || 'Pyrodex';
    const finalTitle = `${title} | ${siteName}`;
    const finalDescription = description || config?.metadata.description || '';

    this.applyRawMetadata(finalTitle, finalDescription);
  }

  /**
   * Lower-level method to apply metadata tags directly.
   */
  private applyRawMetadata(title: string, description: string): void {
    this.titleService.setTitle(title);
    this.metaService.updateTag({ name: 'description', content: description });
    this.metaService.updateTag({ property: 'og:title', content: title });
    this.metaService.updateTag({
      property: 'og:description',
      content: description,
    });
  }

  /**
   * Applies metadata from the current configuration (legacy name, now calls reset).
   */
  applyMetadata(): void {
    this.resetMetadata();
  }

  private updateFavicon(faviconUrl: string): void {
    let link: HTMLLinkElement | null =
      this.document.querySelector("link[rel*='icon']");

    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'icon';
      this.document.head.appendChild(link);
    }

    link.href = faviconUrl;
  }
}
